'use client'

import React from 'react'

import { Box, Skeleton } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { BadgeCounter } from '../BadgeCounter'
import { ChevronRightIcon } from '../Icon/ChevronRightIcon'
import {
  actionsStyles,
  breadcrumbItemStyles,
  breadcrumbStyles,
  containerStyles,
  labelStyles,
  separatorStyles,
  skeletonBg,
} from './TopBar.styles'

export type { ColorScheme }

export type TopBarItem = {
  /** Text shown for this breadcrumb segment. */
  label: string
  /** Optional count rendered as a badge next to the label. */
  count?: number
  /**
   * When true, renders a skeleton placeholder in place of the count badge —
   * for counts that resolve asynchronously. Takes precedence over `count`.
   */
  loading?: boolean
  /**
   * When true, renders a title-sized skeleton in place of the label — for
   * labels that resolve asynchronously (e.g. an org name). Takes precedence
   * over `label`.
   */
  labelLoading?: boolean
}

export type TopBarProps = {
  /**
   * Breadcrumb segments, left to right. A single item renders as a plain page
   * title; multiple items are separated by a chevron.
   */
  items: TopBarItem[]
  /** Right-aligned content, typically action buttons. */
  actions?: React.ReactNode
  colorScheme?: ColorScheme
  /** Rendered as `data-testid` on the outer container for stable selectors. */
  dataTestId?: string
}

/**
 * Top navigation bar per the Design Library "Navigation Bar": a breadcrumb of
 * title(s) with optional count badges on the left, and an actions slot on the
 * right. See the Design Library Foundation (node 2389:3385 light / 1125:367
 * dark).
 */
export const TopBar = ({
  items,
  actions,
  colorScheme = 'dark',
  dataTestId,
}: TopBarProps) => {
  return (
    <Box {...containerStyles(colorScheme)} data-testid={dataTestId}>
      <Box {...breadcrumbStyles}>
        {items.map((item, index) => (
          <React.Fragment key={index}>
            {index > 0 && (
              <Box {...separatorStyles(colorScheme)} aria-hidden>
                <ChevronRightIcon height={16} width={16} />
              </Box>
            )}
            <Box {...breadcrumbItemStyles}>
              {item.labelLoading ? (
                <Skeleton
                  aria-label='Loading title'
                  bg={skeletonBg(colorScheme)}
                  borderRadius='4px'
                  data-testid='TopBar:label-skeleton'
                  height='20px'
                  role='status'
                  width='140px'
                />
              ) : (
                <Box as='span' {...labelStyles(colorScheme)}>
                  {item.label}
                </Box>
              )}
              {item.loading ? (
                <Skeleton
                  aria-label='Loading count'
                  bg={skeletonBg(colorScheme)}
                  borderRadius='8px'
                  data-testid='TopBar:badge-skeleton'
                  height='22px'
                  role='status'
                  width='24px'
                />
              ) : (
                item.count != null && (
                  <BadgeCounter
                    colorScheme={colorScheme}
                    count={item.count}
                    maxCount={Number.MAX_SAFE_INTEGER}
                    variant='incident-topbar'
                  />
                )
              )}
            </Box>
          </React.Fragment>
        ))}
      </Box>
      {actions && <Box {...actionsStyles}>{actions}</Box>}
    </Box>
  )
}
